import React, { useCallback, useContext, useMemo } from "react";
import {
  ACTION,
  PatientPreferredInput,
  RequestListReducerActionTypes,
  RequestListState,
  RequestListType,
} from "./PotentialReceiverTableReducer";

type PotentialReceiverContextType = {
  deselectAllRequests: () => void;
  getRequestState: (id: number) => RequestListType | undefined;
  requestListState: RequestListState;
  setAutomaticSearch: () => void;
  setPatientPreferred: (value: PatientPreferredInput) => void;
  setRequest: (id: number) => void;
};

export const PotentialReceiverContext =
  React.createContext<PotentialReceiverContextType>({
    deselectAllRequests: () => {},
    getRequestState: () => undefined,
    requestListState: { automaticSearch: false, requestFormState: {} },
    setAutomaticSearch: () => {},
    setPatientPreferred: () => {},
    setRequest: () => {},
  });

export function usePotentialReceiverContext(): PotentialReceiverContextType {
  return useContext(PotentialReceiverContext);
}

export function PotentialReceiverContextProvider({
  children,
  dispatch,
  requestListState,
}: {
  children: React.JSX.Element;
  dispatch: React.Dispatch<RequestListReducerActionTypes>;
  requestListState: RequestListState;
}) {
  const deselectAllRequests = useCallback(() => {
    dispatch({ type: ACTION.DESELECT_ALL_REQUESTS });
  }, [dispatch]);

  const getRequestState = useCallback(
    (id: number): RequestListType => {
      const state = requestListState.requestFormState?.[id];
      if (!state) {
        return {
          careprovider_id: -1,
          id,
          manually_selected: false,
          patient_preferred: false,
          patient_preferred_reason: undefined,
        };
      }
      return state;
    },
    [requestListState.requestFormState],
  );

  const setAutomaticSearch = useCallback(() => {
    dispatch({ type: ACTION.SET_AUTOMATIC_SEARCH });
  }, [dispatch]);

  const setPatientPreferred = useCallback(
    (payload: PatientPreferredInput) => {
      dispatch({ payload, type: ACTION.SET_PATIENT_PREFERRED });
    },
    [dispatch],
  );

  const setRequest = useCallback(
    (id: number) => {
      dispatch({ payload: { id }, type: ACTION.SET_REQUEST });
    },
    [dispatch],
  );

  const value = useMemo<PotentialReceiverContextType>(
    () => ({
      deselectAllRequests,
      getRequestState,
      requestListState,
      setAutomaticSearch,
      setPatientPreferred,
      setRequest,
    }),
    [
      deselectAllRequests,
      getRequestState,
      requestListState,
      setAutomaticSearch,
      setPatientPreferred,
      setRequest,
    ],
  );

  return (
    <PotentialReceiverContext.Provider value={value}>
      {children}
    </PotentialReceiverContext.Provider>
  );
}
